import { i18next, Trans, useTranslation } from '@frontend/i18n';
import { useStripeDashboardLink, useMerchants } from '@frontend/payments-hooks';
import { TextLink } from '@frontend/design-system';
import { usePersonLocations } from './use-person-locations';

type DisablingReason = keyof typeof disabledMessages;

const tFn = (message: string) => i18next.t(message, { ns: 'payments' });

const disabledMessages = {
  chargesDisabled: {
    forPerson: {
      single: tFn('Charges are disabled for this location.'),
      multi: tFn('Patient has no selected location with charges enabled.'),
    },
    forList: {
      single: tFn('Charges are disabled for this location.'),
      multi: tFn('Charges are disabled for all selected locations.'),
    },
  },
};

const getErrorMessage = (type: DisablingReason, locationCount: number, personId: string | undefined) =>
  disabledMessages[type][personId ? 'forPerson' : 'forList'][locationCount > 1 ? 'multi' : 'single'];

type UseChargesEnabledLocationsOptions = {
  locationIds?: (string | undefined)[];
  personId?: string;
};

export const useChargesEnabledLocations = ({ locationIds, personId }: UseChargesEnabledLocationsOptions = {}) => {
  const { t } = useTranslation('payments');
  const { personLocationIds, selectedLocationIds, isLoading } = usePersonLocations({
    locationIds,
    personId,
  });

  const { chargesEnabledLocations, noLocationHasChargesEnabled, allMerchantsLoaded } = useMerchants(personLocationIds);
  const onlyOneLocationSelected = selectedLocationIds.length === 1;
  const stripeDashboardLinkHandler = useStripeDashboardLink({ locationIds: selectedLocationIds });

  const getChargesDisabledMessage = (chargesDisabled: boolean, locationId?: string | false) => {
    if (!chargesDisabled) {
      return { disabledMessage: '', disabledMessageWithLink: '' };
    }
    const disabledMessagePrefix = chargesDisabled
      ? getErrorMessage('chargesDisabled', locationId ? 1 : selectedLocationIds.length, personId)
      : '';
    const disabledMessage = disabledMessagePrefix + ' ' + t('Please check your Stripe dashboard.');
    const disabledMessageWithLink = (
      <>
        {locationId ? (
          <Trans t={t}>
            {disabledMessagePrefix} To enable payments again, go to your{' '}
            <TextLink onClick={stripeDashboardLinkHandler(locationId)}>Stripe dashboard</TextLink>
          </Trans>
        ) : (
          disabledMessage
        )}
      </>
    );

    return { disabledMessage, disabledMessageWithLink };
  };

  const getDisabledMessage = (locationId: string) => {
    const chargesDisabled = !chargesEnabledLocations.includes(locationId);
    return getChargesDisabledMessage(chargesDisabled, locationId);
  };

  const { disabledMessage, disabledMessageWithLink } = getChargesDisabledMessage(
    noLocationHasChargesEnabled,
    onlyOneLocationSelected && selectedLocationIds[0]
  );

  const initialLocationId = chargesEnabledLocations.length === 1 ? chargesEnabledLocations[0] : '';

  return {
    chargesEnabledLocations,
    isLoadingChargeableLocations: isLoading || !allMerchantsLoaded,
    disabledMessage,
    disabledMessageWithLink,
    getDisabledMessage,
    selectedLocationIds,
    initialLocationId,
    hasManySelectedLocations: selectedLocationIds.length > 1,
    chargesDisabled: noLocationHasChargesEnabled,
  };
};
